<template>
  <v-dialog v-model="dialog" width="600" persistent>
    <gg-card>
      <gg-toolbar @close="close">
        Disable All Plugins
      </gg-toolbar>
      <gg-card-text>
        <v-row>
          <v-col v-if="loadingPlugins" cols="12" class="text-center">
            <v-progress-circular indeterminate size="64" color="primary" />
          </v-col>
          <v-col v-else cols="12" class="px-2">
            <p class="mb-0" v-if="installation">
              <span v-if="hasPlugins">
                This action will disable all WordPress plugins installed on {{ installation.softdomain }}.
              </span>
              <span v-else>
                There are no active WordPress plugins detected for {{ installation.softdomain }}.
              </span>
            </p>
          </v-col>
        </v-row>
      </gg-card-text>
      <v-card-actions class="px-5 pt-0 pb-3 justify-content-end">
        <gg-btn secondary @click="close">Cancel</gg-btn>
        <gg-btn
          v-show="hasPlugins"
          :loading="submitting"
          @click="deactivate"
        >
          Disable
        </gg-btn>
      </v-card-actions>
    </gg-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import Queue from 'async-await-queue'
import modalMixin from '@/mixins/modal'
import amapi from '@/api/amapi'

export default {
  name: 'DisablePluginsModal',
  mixins: [modalMixin],
  props: {
    installation: {
      type: Object,
      validate (val) {
        return val === null || val.hasOwnProperty('insid')
      }
    }
  },
  data () {
    return {
      submitting: false,
      loadingPlugins: false,
      pluginsToDeactivate: [],
      queue: null
    }
  },
  computed: {
    hasPlugins () {
      return this.pluginsToDeactivate.length > 0
    }
  },
  methods: {
    ...mapActions({
      reportError: 'reportError',
      reportSuccess: 'reportSuccess'
    }),
    async fetchPlugins () {
      this.loadingPlugins = true
      this.pluginsToDeactivate = []
      const payload = {
        insid: this.installation.insid,
        type: 'plugins',
        list: 1
      }
      try {
        const response = await amapi.softaculous_api({
          method: 'wordpress',
          _username: this.$route.query.user,
          payload
        })
        if (!response.plugins_list) {
          throw new Error('Unable to load plugins information.')
        }
        for (const [key, value] of Object.entries(response.plugins_list)) {
          if (value.activated == 1) { // todo: value.activated == 1
            this.pluginsToDeactivate.push(key)
          }
        }
      } catch (e) {
        this.reportError(e)
        this.close()
      } finally {
        this.loadingPlugins = false
      }
    },
    async deactivatePlugin (slug) {
      const payload = {
        insid: this.installation.insid,
        type: 'plugins',
        deactivate: 1,
        slug
      }
      try {
        const response = await amapi.softaculous_api({ method: 'wordpress', payload })
        if (!response.done) {
          throw new Error(response.error[0] ?? 'Unexpected error.')
        }
      } catch (e) {
        console.log(`Deactivation plugin error: ${slug}`)
        this.reportError(e)
      }
    },
    async runQueue () {
      this.queue = new Queue(1, 100)
      for (const plugin of this.pluginsToDeactivate) {
        // eslint-disable-next-line symbol-description
        const task = Symbol()
        await this.queue.wait(task, -1)
        this.deactivatePlugin(plugin)
          .catch(e => console.warn(e))
          .finally(() => this.queue.end(task))
      }
      return this.queue.flush()
    },
    async deactivate () {
      this.submitting = true
      await this.runQueue()
      this.reportSuccess('Plugins disabled.')
      this.submitting = false
      this.close()
    }
  },
  watch: {
    dialog (val) {
      if (val) {
        this.fetchPlugins()
      }
    }
  }
}
</script>
